import { GroupBase, StylesConfig } from 'react-select';
import variables from 'scss/1-settings/colors.module.scss';

const BLUE_500 = variables.blue500; // #1C62EB
const BLUE_100 = variables.blue100; // #DFEAFF
const BLUE_50 = variables.blue50; // #F3F6FC
const GRAY_300 = variables.gray300; // #BBBCC2
const GRAY_200 = variables.gray200; // #E0E0E0
const CLEAR_INDICATOR_WIDTH_PX = 15;
const DEFAULT_INDICATOR_GAP_PX = 2;
const CLEAR_INDICATOR_WIDTH = `${CLEAR_INDICATOR_WIDTH_PX}px`;
const DEFAULT_FONT_SIZE = '0.875rem';

// Additional custom styles for this particular react-select look are located in SelectField.scss under .alternative class
const customStyles = <
  Option extends {},
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  isLink?: boolean,
  isMulti = false,
  isClearable = true,
  showDropdownIndicator = false,
  dropdownIndicatorWidthPx = 15,
  indicatorsGapPx = DEFAULT_INDICATOR_GAP_PX
): StylesConfig<Option, IsMulti, Group> => {
  const inputFontWeight = isLink ? 600 : 'inherit';
  const inputFontSize = isLink ? DEFAULT_FONT_SIZE : 'inherit';
  const inputColor = isLink ? BLUE_500 : 'inherit';
  const isDropdownIndicatorVisible = Boolean(isMulti || showDropdownIndicator);
  const dropdownIndicatorWidth = `${dropdownIndicatorWidthPx}px`;
  const indicatorCount =
    Number(Boolean(isClearable)) + Number(isDropdownIndicatorVisible);
  const clearIndicatorWidth = isClearable ? CLEAR_INDICATOR_WIDTH : 'auto';
  const indicatorsContainerWidth =
    indicatorCount > 0
      ? `${
          (isClearable ? CLEAR_INDICATOR_WIDTH_PX : 0) +
          (isDropdownIndicatorVisible ? dropdownIndicatorWidthPx : 0) +
          (indicatorCount - 1) * indicatorsGapPx
        }px`
      : '0';

  return {
    container: base => ({
      ...base,
      padding: 0,
      margin: '0 4px',
    }),
    control: (base, { isDisabled }) => ({
      ...base,
      border: 'none',
      boxShadow: 'none',
      backgroundColor: isDisabled ? 'transparent' : 'inherit',
      borderRadius: 0,
      borderBottom: `1px solid ${isLink ? BLUE_500 : GRAY_300}`,
      minHeight: '21px',
      '&:hover': {
        borderColor: isLink ? BLUE_500 : GRAY_300,
      },
    }),
    clearIndicator: base => ({
      ...base,
      padding: 0,
      width: clearIndicatorWidth,
    }),
    dropdownIndicator: base => ({
      ...base,
      padding: 0,
      width: dropdownIndicatorWidth,
      display: isDropdownIndicatorVisible ? 'flex' : 'none',
      color: inputColor,
      '&:hover': {
        color: inputColor,
      },
    }),
    indicatorsContainer: base => ({
      ...base,
      display: 'flex',
      alignItems: 'center',
      gap: `${indicatorsGapPx}px`,
      marginLeft: '4px',
      padding: 0,
      width: indicatorsContainerWidth,
    }),
    indicatorSeparator: base => ({
      ...base,
      width: 0,
    }),
    input: base => ({
      ...base,
      padding: 0,
      height: '20px',
      margin: 0,
    }),
    option: (base, { isSelected }) => ({
      ...base,
      background: isSelected ? BLUE_100 : 'inherit',
      color: 'black',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      '&:hover': {
        color: 'black',
        background: BLUE_50,
      },
      cursor: 'pointer',
    }),
    placeholder: base => ({
      ...base,
      fontSize: inputFontSize,
      color: inputColor,
      margin: 0,
      fontWeight: inputFontWeight,
    }),
    singleValue: base => ({
      ...base,
      color: inputColor,
      fontSize: inputFontSize,
      maxWidth: '250px',
      margin: 0,
      fontWeight: inputFontWeight,
    }),
    menu: base => ({
      ...base,
      width: 'max-content',
      maxWidth: '350px',
      borderRadius: '3px',
      border: `1px solid ${GRAY_200}`,
      top: base.top === 0 ? 'inherit' : `calc(${base.top} + 4px)`,
      bottom: base.bottom === 0 ? 'inherit' : `calc(${base.bottom} + 4px)`,
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      zIndex: 9999,
    }),
    menuList: base => ({
      ...base,
      overflowX: 'hidden',
    }),
    valueContainer: base => ({
      ...base,
      padding: 0,
    }),
  };
};

export default customStyles;
